<template>
    <div
        class="flex flex-col items-center relative"
        :class="{ 'z-50 drop-shadow-md': props.isChangeTypePopupOpen }"
        :style="colorStyle"
    >
        <div
            class="bg-(--color-strong-bg)"
            :class="[editWidgetConnectorExtensionsHeightClass, editWidgetConnectorWidthClass]"
        />

        <div
            v-if="showPreviewIcon"
            class="absolute flex justify-center items-center gap-1 rounded-xs drop-shadow-[0_0_0.0625rem_rgba(0,0,0,0.3)] motion-preset-pop motion-duration-300 bg-(--color-strong-bg) text-(--color-icon-text) pointer-events-none"
            :class="[editWidgetIconWrapperSizeClass]"
            :style="[previewIconTopStyle, iconColorStyle]"
        >
            <ReplaceIcon :class="editWidgetIconSizeClass" />
        </div>

        <div
            v-if="props.isChangeTypePopupOpen"
            ref="changeTypePopupRef"
            class="rounded-md border-2 p-2 connect-bar-inverted-border-radius-change-phase-button z-10 after:w-4.5 relative border-(--color-strong-bg) bg-(--color-light-bg) after:bg-(--color-strong-bg) fill-(--color-strong-bg)"
            :style="floatingStyles"
        >
            <!-- Left inverted border radius -->
            <svg class="absolute -bottom-1.5 right-1/2 -translate-x-2.25" viewBox="0 0 100 100" width="4" height="4">
                <mask id="invertedBorderRadiusLeft">
                    <rect x="0" y="0" width="100" height="100" fill="white" />
                    <circle cx="0" cy="100" r="100" fill="black" />
                </mask>

                <rect x="0" y="0" width="100" height="100" mask="url(#invertedBorderRadiusLeft)" />
            </svg>

            <!-- Right inverted border radius -->
            <svg class="absolute -bottom-1.5 left-1/2 translate-x-2.25" viewBox="0 0 100 100" width="4" height="4">
                <mask id="invertedBorderRadiusRight">
                    <rect x="0" y="0" width="100" height="100" fill="white" />
                    <circle cx="100" cy="100" r="100" fill="black" />
                </mask>

                <rect x="0" y="0" width="100" height="100" mask="url(#invertedBorderRadiusRight)" />
            </svg>

            <!-- Filler left -->
            <svg class="absolute -bottom-1 right-1/2 -translate-x-2" viewBox="0 0 100 100" width="1" height="1">
                <rect x="0" y="0" width="100" height="100" />
            </svg>

            <!-- Filler right -->
            <svg class="absolute -bottom-1 left-1/2 translate-x-2" viewBox="0 0 100 100" width="1" height="1">
                <rect x="0" y="0" width="100" height="100" />
            </svg>

            <ChangeTypePopupContent
                :phase-type="props.phaseType"
                :phase-color="props.phaseColor"
                :specializations="props.specializations"
                :selected-specialization="props.selectedSpecialization"
                @change-specialization="(specialization) => emit('change-specialization', specialization)"
                @close-popup="
                    triggerHideTooltip();
                    $emit('toggle-change-phase-button');
                "
            />
        </div>

        <Tooltip placement="bottom" :is-teleported="true">
            <template #trigger="tooltipProps">
                <button
                    ref="changeTypeButtonRef"
                    v-bind="tooltipProps"
                    type="button"
                    class="text-gray-700 rounded-xs flex place-content-center place-items-center border focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-gray-700 cursor-pointer"
                    :class="[
                        colorClasses,
                        editWidgetIconWrapperSizeClass,
                        props.isChangeTypePopupOpen ? 'z-10' : 'drop-shadow-[0_0_0.0625rem_rgba(0,0,0,0.3)]',
                    ]"
                    @click="
                        triggerHideTooltip();
                        $emit('toggle-change-phase-button');
                    "
                    @mouseover="showPreviewIcon = true"
                    @mouseout="showPreviewIcon = false"
                    @focus="showPreviewIcon = true"
                    @blur="showPreviewIcon = false"
                >
                    <ReplaceIcon
                        v-if="props.changePhaseStatus === 'Open-Popup'"
                        class="pointer-events-none"
                        :class="editWidgetIconSizeClass"
                    />
                    <CloseIcon
                        v-if="props.changePhaseStatus === 'Close-Popup'"
                        class="pointer-events-none"
                        :class="editWidgetIconSizeClass"
                    />
                </button>
            </template>

            <template #text>
                <div v-if="props.isChangeTypePopupOpen" class="w-max">
                    {{ $t('close-change-phase-type-popup') }}
                </div>

                <div v-if="!props.isChangeTypePopupOpen" class="w-max">
                    {{ $t('open-change-phase-type-popup') }}
                </div>
            </template>
        </Tooltip>
    </div>
</template>

<script setup lang="ts">
import { autoUpdate, offset, useFloating } from '@floating-ui/vue';
import { computed, provide, ref } from 'vue';
import CloseIcon from '../../Icon/CloseIcon.vue';
import ReplaceIcon from '../../Icon/ReplaceIcon.vue';
import Tooltip from '../../Tooltip/Tooltip.vue';
import { triggerHideTooltipKey } from '../../Tooltip/injectionKeys';
import { PhaseColor, PhaseTypeDatabase } from '../Timeline.types';
import { SpecializationType } from '../allocation/SpecializationMappings';
import {
    get50ColorForPhaseType,
    get900ColorForPhaseType,
    getBaseColorForPhaseType,
    getPlus200ColorForPhaseType,
    getTextColorForPhaseType,
} from '../phaseColors';
import {
    allocationHeightInRem,
    editWidgetConnectorExtensionsHeightClass,
    editWidgetConnectorWidthClass,
    editWidgetIconSizeClass,
    editWidgetIconWrapperSizeClass,
    editWidgetIconWrapperSizeInRem,
} from '../sharedStyles';
import ChangeTypePopupContent from './ChangeTypePopupContent.vue';

type ChangePhaseButtonProps = {
    phaseType: PhaseTypeDatabase;
    phaseColor: PhaseColor;
    changePhaseStatus: 'Open-Popup' | 'Close-Popup' | 'Dont-Show';
    isChangeTypePopupOpen: boolean;
    specializations: (SpecializationType | null)[];
    selectedSpecialization: SpecializationType | null;
    hasTemporaryChangeMarker: boolean;
};

const props = defineProps<ChangePhaseButtonProps>();

const colorStyle = computed(() => ({
    '--color-strong-bg': `var(${getBaseColorForPhaseType(props.phaseColor)})`,
    '--color-text': `var(${getTextColorForPhaseType(props.phaseColor)})`,
    '--color-light-bg': `var(${get50ColorForPhaseType(props.phaseColor)})`,
    '--color-active-bg': `var(${getPlus200ColorForPhaseType(props.phaseColor)})`,
    '--color-active-border': `var(${get900ColorForPhaseType(props.phaseColor)})`,
}));

const colorClasses = `
    bg-(--color-light-bg) border-(--color-strong-bg) text-(--color-text)
    hover:bg-(--color-strong-bg) hover:text-(--color-text)
    active:bg-(--color-active-bg) active:text-gray-50 active:border-(--color-active-border)
`;

const emit = defineEmits<{
    (e: 'toggle-change-phase-button'): void;
    (e: 'change-specialization', specialization: SpecializationType | null): void;
}>();

// This is used to force close the Tooltip after clicking the button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}

const iconColorStyle = computed(() => {
    return {
        '--color-icon-text': `var(${getTextColorForPhaseType(props.phaseColor)})`,
    };
});

const changeTypeButtonRef = ref();
const changeTypePopupRef = ref();

const { floatingStyles } = useFloating(changeTypeButtonRef, changeTypePopupRef, {
    open: props.isChangeTypePopupOpen,
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware: [offset(0)],
});

const showPreviewIcon = ref(false);

const previewIconTopStyle = computed(() => {
    const differenceIconAllocationHeightInRem = (allocationHeightInRem - editWidgetIconWrapperSizeInRem) / 2;

    return {
        top: `-${allocationHeightInRem - differenceIconAllocationHeightInRem}rem`,
    };
});
</script>

<style scoped>
.connect-bar-inverted-border-radius-change-phase-button {
    position: relative;
}

.connect-bar-inverted-border-radius-change-phase-button::after {
    content: '';
    position: absolute;
    height: 1px;
    bottom: -3px;
    left: 50%;
    transform: translate(-50%);
}
</style>
